import {
  composeGid,
  composeGidFactory,
  parseGid,
} from '@shopify/admin-graphql-api-utilities';
import {ChangeEvent, useReducer} from 'react';
import {camelCase} from 'lodash';

import {btoa, atob} from '../../@shopify/encoders';
import type {TypeName, TypeNameMap} from '../types';

// tslint:disable-next-line:no-empty
export function noop() {}

export function noopPromise() {
  return Promise.resolve();
}

export function identity<T>(val: T): T {
  return val;
}
interface ParsedGidWithParams {
  id: string;
  params: {[key: string]: string};
}

const GID_TYPE_REGEXP = /\/(\w+)\/([a-zA-Z0-9+_.$%-]+)(\?.*)?$/;
const GID_PREFIX = 'gid://shopify/';
const VALID_REGEXP_OPTIONS = ['g', 'i', 'm', 's', 'u', 'y'];

// don't export this since it must be encoded
const composeOrganizationGid = composeGidFactory('organization');

export {composeGid, parseGid};

export function isGid(gid?: string): boolean {
  if (gid == null) {
    return false;
  }
  return gid.startsWith(GID_PREFIX);
}

/**
 * Encodes an id into a Base64 encoded GID with gid://organization/{type}/{id} prefix.
 * @param {string} key The type for the encoded GID.
 * @param {(number | string)} id The id for the encoded GID.
 * @returns {string} The composed and Base64 encoded GID.
 */
export function composeEncodedOrganizationGid(
  key: string,
  id: number | string,
): string {
  return btoa(composeOrganizationGid(key, id));
}

/**
 * Parses a Base64 encoded GID
 * @param {string} gid The encoded GID.
 * @returns {string} The GID ID.
 */
export function parseEncodedGid(gid: string): string {
  const decoded = atob(gid);
  return parseGid(decoded);
}

export function parseGidType(gid: string): string {
  // appends forward slash to help identify invalid id
  const id = `/${gid}`;
  const matches = id.match(GID_TYPE_REGEXP);

  if (matches && matches[1] !== undefined) {
    return matches[1];
  }
  throw new Error(`Invalid gid: ${gid}`);
}

export function parseGidWithParams(gid: string): ParsedGidWithParams {
  // appends forward slash to help identify invalid id
  const id = `/${gid}`;
  const matches = GID_TYPE_REGEXP.exec(id);
  if (matches && matches[2] !== undefined) {
    const params =
      matches[3] === undefined
        ? {}
        : fromEntries(new URLSearchParams(matches[3]).entries());

    return {
      id: matches[2],
      params,
    };
  }
  throw new Error(`Invalid gid: ${gid}`);
}

export function shallowEqual(
  firstObject: {[key: string]: any},
  secondObject: {[key: string]: any},
) {
  const unequalKeys = Object.keys(firstObject).filter((key) => {
    return firstObject[key] !== secondObject[key];
  });

  return unequalKeys.length === 0;
}

export function reorderArray(
  array: any[],
  initalIndex: number,
  newIndex: number,
) {
  array.splice(newIndex, 0, array.splice(initalIndex, 1)[0]);
  return array;
}

export function removeDuplicates(array: any[], key: string) {
  const map = new Map();
  array.forEach((item) => {
    if (!map.has(item[key])) {
      map.set(item[key], item);
    }
  });
  return [...map.values()];
}

export function outsideOfContainer(
  xPos: number,
  yPos: number,
  element: HTMLElement,
) {
  const rect = element.getBoundingClientRect();

  return rect
    ? xPos >= rect.left + rect.width ||
        xPos <= rect.left ||
        yPos >= rect.top + rect.height ||
        yPos <= rect.top
    : false;
}

export function storefrontProductURL(storefrontURL: string) {
  const url = new URL(storefrontURL);
  url.search = '';
  url.pathname = '/products/';
  return url.toString();
}

export function storefrontCollectionURL(storefrontURL: string) {
  const url = new URL(storefrontURL);
  url.search = '';
  url.pathname = '/collections/';
  return url.toString();
}

export function validateNumericString(input: string) {
  return input !== '' && (input.match(/[^0-9.,]/g) || []).length === 0;
}

export const DEFAULT_PAGINATION_LIMIT = 50;

export function isChangeEvent(
  value: any,
): value is ChangeEvent<HTMLInputElement> {
  return (
    typeof value === 'object' &&
    Reflect.has(value, 'target') &&
    Reflect.has(value.target, 'value')
  );
}

// Make sure you understand how setImmediate works before using it.
// The promise created by this method resolves after all Synchronous operations
// and everything in the current event loop has been evaluated.
export function nextEventLoopTick() {
  return new Promise(setImmediate);
}

/**
 * Type guard for property membership.
 * @description
 * const a: any = {};
 * if (has(a, 'foobar', 'string') && has(a, 'foobaz', 'number')) {
 *     const b: {foobar: string, foobaz: number} = a; // TS knows these keys exists there
 * }
 * @examples
 * has({foobar:'ok'}, 'foobar', 'string') // => true
 * has({foobar:3}, 'foobar', 'string') // => false
 * has({}, 'foobar', 'string') // => false
 */
export function has<K extends PropertyKey, T extends TypeName>(
  obj: any,
  key: K,
  type: T,
): obj is {[P in K]: TypeNameMap[T]} {
  const haveKeys = typeof obj === 'object' && obj !== null;
  return haveKeys && key in obj && type === typeof obj[key];
}
export function useLegacySetState<T>(defaultState: T) {
  // mimic the legacy behavior of this.setState in react
  // Note: The intent is to help transitionning from React components
  // Please simply use `const [val, setVal] = useState()` when possible
  return useReducer(
    (previousState: T, newState: Partial<T>): T => ({
      ...previousState,
      ...newState,
    }),
    defaultState,
  );
}

export function lastArrayItem<T>(item: T[]): T | undefined {
  if (item.length === 0) {
    return undefined;
  }

  return item[item.length - 1];
}

export function toValidRegExpOptions(options: any): string {
  if (!options) {
    return '';
  }

  for (const option of options) {
    if (!VALID_REGEXP_OPTIONS.includes(option)) {
      return '';
    }
  }

  return options;
}

function fromEntries<K extends string, T>(entries: IterableIterator<[K, T]>) {
  const obj = {} as {[key: string]: T};
  for (const [key, val] of entries) {
    const newKey = camelCase(key);
    obj[newKey] = val;
  }
  return obj;
}
